import { useGrippContext } from '@gripp/shared-logic';
import { ReactNode, useState } from 'react';
import { Pressable } from 'react-native';
import { MobileIcon } from '../../icons';
import { colors, spacing } from '../../tokens';
import { TextInput, TextInputProps } from '../TextInput';

export interface BarcodeScanInputProps
  extends Omit<TextInputProps, 'rightIcon'> {
  /** Called when a value is scanned or typed */
  onScanned?: (value: string) => void;
  /** Override the default right icon (e.g. to show a remove icon when filled) */
  rightIcon?: ReactNode;
  /** Called when scan icon is pressed. When provided, the component does not
   *  render its own scanner overlay — the parent is responsible for that. */
  onScanPress?: () => void;
}

export const BarcodeScanInput = ({
  onScanned,
  onChangeText,
  rightIcon: rightIconOverride,
  onScanPress,
  ...props
}: BarcodeScanInputProps) => {
  const { barcodeScannerComponent: BarcodeScanner } = useGrippContext();
  const [isScannerVisible, setIsScannerVisible] = useState(false);

  const handleChangeText = (text: string) => {
    onChangeText?.(text);
  };

  const handleScanned = (value: string) => {
    onScanned?.(value);
    onChangeText?.(value);
    setIsScannerVisible(false);
  };

  const defaultRightIcon = BarcodeScanner ? (
    <Pressable
      testID="barcode-scan-button"
      onPress={onScanPress ?? (() => setIsScannerVisible(true))}
      hitSlop={spacing.sm}
      accessibilityRole="button"
    >
      <MobileIcon size={spacing.md} color={colors.neutral[500]} />
    </Pressable>
  ) : (
    <MobileIcon size={spacing.md} color={colors.neutral[500]} />
  );

  // Only render internal scanner when we manage it ourselves (no onScanPress)
  const managesScanner = !onScanPress;

  return (
    <>
      <TextInput
        {...props}
        onChangeText={handleChangeText}
        rightIcon={rightIconOverride ?? defaultRightIcon}
      />
      {managesScanner && BarcodeScanner && isScannerVisible && (
        <BarcodeScanner
          onScanned={handleScanned}
          onDismiss={() => setIsScannerVisible(false)}
        />
      )}
    </>
  );
};
